import { hydrateMapData, show3dMap } from '@mappedin/mappedin-js';
import type { VenueOSClient } from '@mappedin/venueos-applet';
import type { MappedinMapData, MappedinMapView, OutdoorViewOptions } from './mappedin-types';

export interface Venue {
  id: string;
  name: string;
  latitude: number;
  longitude: number;
  slug?: string;
  city?: string;
  state?: string;
}

export interface LoadVenueMapOptions {
  outdoorView?: { enabled: boolean } | (OutdoorViewOptions & { enabled: boolean });
  multiFloorView?: { enabled: boolean; floorGap?: number | 'auto'; footprintColor?: string };
}

export interface LoadVenueMapResult {
  mapView: MappedinMapView;
  mapData: MappedinMapData;
}

/**
 * Download an MVF for a venue and render it with Mappedin's `show3dMap`,
 * matching the App Builder runtime's `VenueOSMapHelpers.loadVenueMap`.
 *
 * This returns the raw `MapView` + `MapData` so the caller can drive
 * floor stacking, camera, plugins, and marker layers directly (the
 * "venue overview" app leans on all of those).
 */
export async function loadVenueMap(
  client: VenueOSClient,
  container: HTMLElement,
  venue: Venue,
  opts?: LoadVenueMapOptions,
): Promise<LoadVenueMapResult> {
  const mapInfo = await client.getMapData({ mapId: venue.id });

  const mvfResponse = await fetch(mapInfo.mvfUrl);
  if (!mvfResponse.ok) {
    throw new Error(`Failed to download MVF: ${mvfResponse.status}`);
  }
  const zipBuffer = new Uint8Array(await mvfResponse.arrayBuffer());

  const mapData = (await hydrateMapData({
    type: 'binary',
    main: zipBuffer,
  })) as unknown as MappedinMapData;

  // Match the VenueOS App Builder's `VenueOSMapHelpers.loadVenueMap`
  // exactly: shallow-copy the caller's opts and only fill in
  // `outdoorView` with tiles credentials if the caller hasn't already
  // provided one. Critically, if the caller passes
  // `outdoorView: { enabled: false }` (as the Venue Overview does), we
  // forward that unchanged — enabling the MapLibre outdoor context when
  // the caller asked for it off interferes with the manual floor
  // stacking.
  const showOptions: Record<string, unknown> = { ...(opts ?? {}) };

  if (!showOptions['outdoorView']) {
    let tiles = mapInfo.tiles ?? null;
    if (!tiles) {
      try {
        tiles = await client.getTilesConfig();
      } catch {
        tiles = null;
      }
    }
    if (tiles) {
      const outdoorView: OutdoorViewOptions & { enabled: true } = {
        token: tiles.key,
        style: tiles.styleUrl,
        enabled: true,
      };
      showOptions['outdoorView'] = outdoorView;
    }
  }

  const mapView = (await show3dMap(
    container,
    mapData as unknown as Parameters<typeof show3dMap>[1],
    showOptions as Parameters<typeof show3dMap>[2],
  )) as unknown as MappedinMapView;

  return { mapView, mapData };
}

/**
 * Escape arbitrary strings for safe interpolation into the HTML marker
 * templates this app uses. Mirrors `VenueOSMapHelpers.escapeHtml`.
 */
export function escapeHtml(text: string): string {
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Linearly blend two hex colors. `amount=0` returns `a`, `amount=1`
 * returns `b`. Matches the `VenueOSMapHelpers.interpolateColor` used by
 * the Structural Pipes plugin.
 */
export function interpolateColor(a: string, b: string, amount: number): string {
  const t = Math.max(0, Math.min(1, amount));
  const ax = parseHex(a);
  const bx = parseHex(b);
  if (!ax || !bx) return a;
  const r = Math.round(ax.r + (bx.r - ax.r) * t);
  const g = Math.round(ax.g + (bx.g - ax.g) * t);
  const bl = Math.round(ax.b + (bx.b - ax.b) * t);
  return `#${toHex(r)}${toHex(g)}${toHex(bl)}`;
}

function parseHex(hex: string): { r: number; g: number; b: number } | null {
  if (!hex || typeof hex !== 'string') return null;
  let h = hex.replace('#', '');
  if (h.length === 3) {
    const h0 = h[0] ?? '';
    const h1 = h[1] ?? '';
    const h2 = h[2] ?? '';
    h = h0 + h0 + h1 + h1 + h2 + h2;
  }
  if (h.length !== 6) return null;
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) return null;
  return { r, g, b };
}

function toHex(n: number): string {
  const s = Math.max(0, Math.min(255, n)).toString(16);
  return s.length === 1 ? `0${s}` : s;
}

/**
 * Return a heatmap gradient color for `intensity` in `[0, 1]`, matching
 * the App Builder runtime's `VenueOSMapHelpers.getHeatmapColor`.
 * 0 ≈ cool blue, 1 ≈ hot red.
 */
export function getHeatmapColor(intensity: number): string {
  const t = Math.max(0, Math.min(1, intensity));
  const stops: Array<[number, [number, number, number]]> = [
    [0.0, [59, 130, 246]],
    [0.35, [34, 211, 238]],
    [0.6, [250, 204, 21]],
    [0.85, [251, 146, 60]],
    [1.0, [239, 68, 68]],
  ];
  for (let i = 0; i < stops.length - 1; i++) {
    const a = stops[i];
    const b = stops[i + 1];
    if (!a || !b) continue;
    if (t >= a[0] && t <= b[0]) {
      const f = (t - a[0]) / (b[0] - a[0]);
      const r = Math.round(a[1][0] + (b[1][0] - a[1][0]) * f);
      const g = Math.round(a[1][1] + (b[1][1] - a[1][1]) * f);
      const bl = Math.round(a[1][2] + (b[1][2] - a[1][2]) * f);
      return `rgb(${r},${g},${bl})`;
    }
  }
  return 'rgb(59,130,246)';
}
